import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, Megaphone, Drama, TrendingUp, Gauge, GitBranch, GitMerge,
  Map as MapIcon, AlertTriangle, Trophy, MoreVertical, Cloud,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid, Legend } from "recharts";
import { apiClient } from "../lib/api";

const TABS = ["DOMINANCE", "TRENDS", "NETWORK", "NARRATIVES", "GEOSPATIAL", "IMPACT"];
const PERIODS = [
  { label: "Seminggu Terakhir", value: "week" },
  { label: "Sebulan Terakhir", value: "month" },
  { label: "3 Bulan Terakhir", value: "3month" },
];

const Card = ({ icon, title, sub, color = "#facc15", children, testId, className = "" }) => (
  <div className={`mip-card ${className}`} data-testid={testId}>
    <div className="mip-card-head">
      <div className="mip-card-icon" style={{ color, background: `${color}1a`, borderColor: `${color}55` }}>
        {icon}
      </div>
      <div className="mip-card-titles">
        <h3>{title}</h3>
        {sub && <p>{sub}</p>}
      </div>
      <button className="mip-card-more" aria-label="More" type="button"><MoreVertical size={14} /></button>
    </div>
    <div className="mip-card-body">{children}</div>
  </div>
);

// Color palette for topic cloud words
const CLOUD_COLORS = ["#10b981", "#14b8a6", "#06b6d4", "#3b82f6", "#a78bfa", "#facc15", "#f59e0b", "#ef4444", "#ec4899", "#94a3b8"];

// Color palette for province cards
const PROV_GRADIENTS = [
  ["#3b82f6", "#1e3a8a"], ["#06b6d4", "#0e7490"], ["#10b981", "#065f46"],
  ["#a78bfa", "#5b21b6"], ["#f59e0b", "#92400e"], ["#ec4899", "#9d174d"],
];

export default function MediaInsightPanel({ onBack }) {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("DOMINANCE");
  const [period, setPeriod] = useState("week");
  const [pending, setPending] = useState("week");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/media-stats?period=${period}`)
      .then((r) => setStats(r.data?.data || null))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [period]);

  const num = (v) => Number(v ?? 0);

  // -------- DOMINANCE --------
  const shareOfVoice = useMemo(() => (stats?.shareOfVoice || []).slice(0, 12), [stats]);
  const maxArticles = useMemo(
    () => Math.max(1, ...shareOfVoice.map((d) => num(d.articles))),
    [shareOfVoice]
  );

  // Sentiment Bias rows from same shareOfVoice (top sources w/ classified counts)
  const sentimentBias = useMemo(() => (stats?.shareOfVoice || []).slice(0, 16), [stats]);
  const maxSentTotal = useMemo(
    () => Math.max(1, ...sentimentBias.map((d) =>
      num(d.sentiment?.positive) + num(d.sentiment?.negative) + num(d.sentiment?.neutral) + num(d.sentiment?.others)
    )),
    [sentimentBias]
  );

  // -------- TRENDS --------
  // Sentiment Dynamics (7 days)
  const sentimentDynamics = useMemo(() => {
    return (stats?.sentimentTrend || []).map((r) => {
      const d = new Date(r.day);
      return {
        label: `${d.getDate()}/${d.getMonth() + 1}`,
        positive: num(r.positive),
        negative: num(r.negative),
        neutral: num(r.neutral),
      };
    });
  }, [stats]);

  // Publishing Velocity (hourly buckets)
  const publishingVelocity = useMemo(() => {
    return (stats?.velocity || []).map((r) => {
      const d = new Date(r.hour);
      return {
        label: `${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:00`,
        count: num(r.count),
      };
    });
  }, [stats]);

  // -------- NETWORK --------
  const sourceNetwork = useMemo(() => (stats?.sourceNetwork || []).slice(0, 10), [stats]);
  const maxAmp = useMemo(
    () => Math.max(1, ...sourceNetwork.map((d) => num(d.amplification_reach))),
    [sourceNetwork]
  );

  const divergence = useMemo(() => {
    return (stats?.mediaDivergence || [])
      .filter((d) => num(d.pos_domains) + num(d.neg_domains) > 0)
      .slice(0, 8)
      .map((d) => ({
        title: d.issue_name,
        pro: num(d.pos_domains),
        con: num(d.neg_domains),
        total: num(d.total_domains),
        ratio: d.conflict_ratio,
      }));
  }, [stats]);

  // -------- NARRATIVES --------
  const topicCloud = useMemo(() => {
    const list = stats?.topicCloud || [];
    if (!list.length) return [];
    const maxVal = Math.max(...list.map((w) => num(w.value)));
    const minVal = Math.min(...list.map((w) => num(w.value)));
    const range = Math.max(1, maxVal - minVal);
    return list.slice(0, 80).map((w, i) => {
      const v = num(w.value);
      const norm = (v - minVal) / range; // 0..1
      const size = 12 + norm * 44;
      return {
        text: w.text,
        value: v,
        size,
        color: CLOUD_COLORS[i % CLOUD_COLORS.length],
        opacity: 0.55 + norm * 0.45,
      };
    });
  }, [stats]);

  // -------- GEOSPATIAL --------
  const heatmap = useMemo(() => (stats?.heatmap || []).slice(0, 30), [stats]);
  const regionalCritical = useMemo(() => (stats?.regionalCritical || []).slice(0, 10), [stats]);
  const maxCriticalCount = useMemo(
    () => Math.max(1, ...regionalCritical.map((d) => num(d.critical_count))),
    [regionalCritical]
  );

  // -------- IMPACT --------
  const significance = useMemo(() => (stats?.significance || []).slice(0, 15), [stats]);
  const maxSig = useMemo(
    () => Math.max(1, ...significance.map((d) => num(d.high_sig_count))),
    [significance]
  );

  const onSubmit = () => setPeriod(pending);

  return (
    <div className="mip-page" data-testid="media-insight-page">
      <header className="mip-head">
        <button className="mip-back" onClick={onBack} data-testid="mip-back" type="button">
          <ArrowLeft size={16} /> KEMBALI KE DASHBOARD
        </button>
      </header>

      <div className="mip-hero">
        <div>
          <h1 className="mip-title">MEDIA INSIGHT PANEL</h1>
          <p className="mip-sub">Monitoring Dominasi, Sentimen &amp; Kapasitas Portal Berita</p>
        </div>
        <div className="mip-controls">
          <select
            className="mip-period"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            data-testid="mip-period"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <button type="button" className="mip-submit" onClick={onSubmit} data-testid="mip-submit">SUBMIT</button>
        </div>
      </div>

      <nav className="mip-tabs" data-testid="mip-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`mip-tab ${tab === t ? "active" : ""}`}
            onClick={() => setTab(t)}
            data-testid={`mip-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </nav>

      <div className="mip-content">
        {loading && <div className="mip-loading" data-testid="mip-loading">Memuat data media…</div>}

        {!loading && tab === "DOMINANCE" && (
          <div className="mip-grid-2">
            <Card icon={<Megaphone size={16} />} title="SHARE OF VOICE" sub="DOMINASI MEDIA (VOLUME BERITA)" testId="card-share">
              <div className="mip-vbars">
                {shareOfVoice.map((d) => (
                  <div key={d.domain} className="mip-vbar-row" data-testid={`share-${d.domain}`}>
                    <div className="mip-vbar-head">
                      <span className="mip-vbar-label">{d.domain}</span>
                      <span className="mip-vbar-val">{num(d.articles).toLocaleString()} <em>art</em></span>
                    </div>
                    <div className="mip-vbar-track">
                      <div className="mip-vbar-fill" style={{ width: `${(num(d.articles) / maxArticles) * 100}%` }} />
                    </div>
                  </div>
                ))}
                {!shareOfVoice.length && <div className="mip-empty">Tidak ada data.</div>}
              </div>
            </Card>

            <Card icon={<Drama size={16} />} title="SENTIMENT BIAS" sub="KECENDERUNGAN BERITA (POSITIF/NEGATIF)" testId="card-sentiment-bias">
              <div className="mip-sentbias">
                {sentimentBias.map((s) => {
                  const pos = num(s.sentiment?.positive);
                  const neg = num(s.sentiment?.negative);
                  const neu = num(s.sentiment?.neutral);
                  const oth = num(s.sentiment?.others);
                  const total = pos + neg + neu + oth || 1;
                  const W = (v) => `${(v / maxSentTotal) * 100}%`;
                  return (
                    <div key={s.domain} className="mip-sb-row" data-testid={`bias-${s.domain}`}>
                      <div className="mip-sb-label" title={s.domain}>{s.domain}</div>
                      <div className="mip-sb-bar" title={`pos=${pos} neg=${neg} neu=${neu} other=${oth}`}>
                        <div className="seg pos" style={{ width: W(pos) }} />
                        <div className="seg neg" style={{ width: W(neg) }} />
                        <div className="seg neu" style={{ width: W(neu) }} />
                        <div className="seg empty" style={{ width: W(oth) }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mip-legend">
                <span><i className="dot pos" /> POSITIF</span>
                <span><i className="dot neg" /> NEGATIF</span>
                <span><i className="dot neu" /> NETRAL</span>
                <span><i className="dot oth" /> OTHERS</span>
              </div>
            </Card>
          </div>
        )}

        {!loading && tab === "TRENDS" && (
          <div className="mip-grid-2">
            <Card icon={<TrendingUp size={16} />} title="SENTIMENT DYNAMICS (7 Days)" sub="PERGERAKAN TREN SENTIMEN MEDIA" testId="card-trends">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={sentimentDynamics} margin={{ top: 10, right: 10, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="label" stroke="#475569" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis stroke="#475569" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <RTooltip contentStyle={{ background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 8 }} />
                  <Bar dataKey="positive" name="POSITIF" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="neutral" name="NETRAL" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="negative" name="NEGATIF" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mip-legend">
                <span><i className="dot pos" /> POSITIF</span>
                <span><i className="dot neu" /> NETRAL</span>
                <span><i className="dot neg" /> NEGATIF</span>
              </div>
            </Card>

            <Card icon={<Gauge size={16} />} title="PUBLISHING VELOCITY" sub="INTENSITAS PENERBITAN BERITA" testId="card-velocity">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={publishingVelocity} margin={{ top: 10, right: 10, left: 0, bottom: 8 }}>
                  <defs>
                    <linearGradient id="vel-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" />
                      <stop offset="100%" stopColor="#0e7490" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="label" stroke="#475569" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={Math.max(1, Math.floor(publishingVelocity.length / 12))} />
                  <YAxis stroke="#475569" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <RTooltip contentStyle={{ background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 8 }} />
                  <Bar dataKey="count" fill="url(#vel-grad)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        )}

        {!loading && tab === "NETWORK" && (
          <div className="mip-grid-2">
            <Card icon={<GitBranch size={16} />} title="SOURCE NETWORK INTELLIGENCE" sub="ORIGINATOR VS AMPLIFIER RELATIONS" testId="card-network">
              <div className="mip-net-list">
                {sourceNetwork.map((s) => (
                  <div key={s.domain} className="mip-net-card" data-testid={`net-${s.domain}`}>
                    <div className="mip-net-name">{s.domain}</div>
                    <div className="mip-net-row">
                      <span className="mip-pill blue">ORIGINATOR</span>
                      <span className="mip-pill purple">{num(s.network_size)} PORTALS REACHED</span>
                      <span className="mip-net-num">{num(s.amplification_reach).toLocaleString()}</span>
                      <span className="mip-net-lbl">AMPLIFIED ARTICLES</span>
                    </div>
                    <div className="mip-net-bar">
                      <div className="mip-net-bar-fill" style={{ width: `${(num(s.amplification_reach) / maxAmp) * 100}%` }} />
                    </div>
                  </div>
                ))}
                {!sourceNetwork.length && <div className="mip-empty">Tidak ada data jaringan sumber.</div>}
              </div>
            </Card>

            <Card icon={<GitMerge size={16} />} title="MEDIA DIVERGENCE ANALYSIS" sub="CONTROVERSIAL ISSUES (POSITIVE VS NEGATIVE SPLIT)" color="#ec4899" testId="card-divergence">
              <div className="mip-div-list">
                {divergence.map((d, i) => (
                  <div key={i} className="mip-div-row" data-testid={`div-${i}`}>
                    <div className="mip-div-title">{d.title}</div>
                    <div className="mip-div-bar">
                      <div className="dv pro" style={{ flex: Math.max(1, d.pro) }}><strong>{d.pro}</strong></div>
                      <div className="dv con" style={{ flex: Math.max(1, d.con) }}><strong>{d.con}</strong></div>
                    </div>
                    <div className="mip-div-foot">
                      <span className="pro-lbl">PRO MEDIA</span>
                      <span className="con-lbl">CONTRA MEDIA</span>
                    </div>
                  </div>
                ))}
                {!divergence.length && <div className="mip-empty">Tidak ada isu kontroversial.</div>}
              </div>
            </Card>
          </div>
        )}

        {!loading && tab === "NARRATIVES" && (
          <Card icon={<Cloud size={16} />} title="NARRATIVE TOPIC CLOUD" sub="DOMINANT ISSUES & KEYWORDS (TOP 80)" color="#a78bfa" testId="card-narratives">
            <div className="mip-cloud" data-testid="topic-cloud">
              {topicCloud.map((w) => (
                <span
                  key={w.text}
                  className="cloud-word"
                  style={{ fontSize: `${w.size}px`, color: w.color, opacity: w.opacity }}
                  title={`${w.text} · ${w.value.toLocaleString()}`}
                >
                  {w.text}
                </span>
              ))}
              {!topicCloud.length && <div className="mip-empty">Tidak ada keyword.</div>}
            </div>
          </Card>
        )}

        {!loading && tab === "GEOSPATIAL" && (
          <div className="mip-geo-grid">
            <Card icon={<MapIcon size={16} />} title="COVERAGE HEATMAP" sub="GEOSPATIAL DISTRIBUTION (MEDIA × REGION)" testId="card-heatmap">
              <div className="mip-heatmap">
                {heatmap.map((h, i) => {
                  const [c1, c2] = PROV_GRADIENTS[i % PROV_GRADIENTS.length];
                  return (
                    <div
                      key={`${h.news_domain}-${i}`}
                      className="mip-heat-cell"
                      style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                      data-testid={`heat-${i}`}
                    >
                      <div className="mhc-prov">{h.region}</div>
                      <div className="mhc-source">{h.news_domain}</div>
                      <div className="mhc-count">{num(h.count).toLocaleString()}</div>
                    </div>
                  );
                })}
                {!heatmap.length && <div className="mip-empty">Tidak ada data heatmap.</div>}
              </div>
            </Card>

            <Card icon={<AlertTriangle size={16} />} title="REGIONAL CRITICAL INDEX" sub="NEGATIVE SENTIMENT × HIGH SIGNIFICANCE" color="#ef4444" testId="card-critical">
              <div className="mip-crit-list">
                {regionalCritical.map((r) => {
                  const w = (num(r.critical_count) / maxCriticalCount) * 100;
                  const sev = parseFloat(r.avg_severity);
                  const sevColor = sev >= 4 ? "#ef4444" : sev >= 3 ? "#f59e0b" : "#facc15";
                  return (
                    <div key={r.region} className="mip-crit-card" data-testid={`crit-${r.region}`}>
                      <div className="mip-crit-name">{r.region}</div>
                      <div className="mip-crit-row">
                        <div className="mip-crit-bar">
                          <div style={{ width: `${w}%`, background: sevColor }} />
                        </div>
                        <div className="mip-crit-vals">
                          <span className="mc-num">{num(r.critical_count).toLocaleString()}</span>
                          <span className="mc-lbl">INCIDENTS</span>
                        </div>
                        <div className="mip-crit-avg">
                          <span style={{ color: sevColor }}>{sev.toFixed(2)}</span>
                          <span className="mc-lbl">AVG</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!regionalCritical.length && <div className="mip-empty">Tidak ada wilayah kritis.</div>}
              </div>
            </Card>
          </div>
        )}

        {!loading && tab === "IMPACT" && (
          <Card icon={<Trophy size={16} />} title="IMPACT LEADERBOARD" sub="HIGH-SIGNIFICANCE ISSUES (AI ASSESSMENT)" color="#f59e0b" testId="card-impact">
            <div className="mip-impact-list">
              {significance.map((row, i) => (
                <div key={row.news_domain} className="mip-impact-row" data-testid={`impact-${i + 1}`}>
                  <span className="mi-rank">#{i + 1}</span>
                  <span className="mi-source">{row.news_domain}</span>
                  <div className="mi-bar">
                    <div className="mi-bar-fill" style={{ width: `${(num(row.high_sig_count) / maxSig) * 100}%` }} />
                  </div>
                  <span className="mi-num">{num(row.high_sig_count)}</span>
                  <span className="mi-lbl">ISSUES</span>
                </div>
              ))}
              {!significance.length && <div className="mip-empty">Tidak ada data leaderboard.</div>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
